import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Mic, MicOff, Phone, PhoneOff, Square, MessageCircle, AudioLines, BarChart3 } from 'lucide-react';

import { kioskApi } from '../../api/kiosk.js';
import { chatApi } from '../../api/chat.js';
import { ApiError } from '../../api/client.js';
import { Card } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Tabs } from '../../components/ui/Tabs.jsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.jsx';
import { Composer } from '../../components/ai/Composer.jsx';
import { MessageBubble } from '../../components/ai/MessageBubble.jsx';
import { VoiceOrb } from '../../components/ai/VoiceOrb.jsx';
import { TranscriptPanel } from '../../components/ai/TranscriptPanel.jsx';
import { useRealtimeVoice } from '../../hooks/useRealtimeVoice.js';
import { t } from '../../lib/i18n.js';

const STORAGE_KEY = 'orme:kiosk:session';

// Customer friendly labels for the consent purposes: the raw codes
// (ai_chat, voice_chat, analytics) are never shown to the visitor.
const PURPOSE_LABEL = {
  ai_chat: { label: 'Assistente attivo', Icon: MessageCircle },
  voice_chat: { label: 'Voce abilitata', Icon: AudioLines },
  analytics: { label: 'Statistiche anonime', Icon: BarChart3 }
};

export function KioskChat({ session, onSessionEnded }) {
  const purposes = session.purposes || [];

  // Voice availability for kiosks is set in admin. Combined with the visitor's
  // voice consent it decides whether the voice mode is offered at all.
  const voiceConfigQuery = useQuery({
    queryKey: ['ai', 'voice-config'],
    queryFn: chatApi.voiceConfig,
    staleTime: 30_000
  });
  const voiceEnabled = Boolean(voiceConfigQuery.data && voiceConfigQuery.data.enabled);
  const voiceAllowed = voiceEnabled && purposes.includes('voice_chat');

  const [mode, setMode] = useState('text');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const listRef = useRef(null);
  const abortRef = useRef(null);

  const voice = useRealtimeVoice();
  const voiceLive = voice.status === 'live' || voice.status === 'connecting';

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const patchLastAssistant = (patch) => setMessages((prev) => {
    const next = [...prev];
    const last = next[next.length - 1];
    if (last && last.role === 'assistant') next[next.length - 1] = typeof patch === 'function' ? patch(last) : { ...last, ...patch };
    return next;
  });

  // Streaming send over the customer-session SSE transport.
  const sendText = async (text) => {
    if (!text || sending) return;
    setSending(true);
    setMessages((prev) => [...prev,
      { role: 'user', content: text },
      { role: 'assistant', content: '', streaming: true }
    ]);
    const controller = new AbortController();
    abortRef.current = controller;
    let stopped = false;
    try {
      const done = await kioskApi.chatStream(
        { message: text, conversationId: conversationId || undefined },
        {
          customerSessionId: session.customerSessionId,
          signal: controller.signal,
          onDelta: (chunk) => patchLastAssistant((last) => ({ ...last, content: last.content + chunk }))
        }
      );
      patchLastAssistant((last) => ({
        ...last,
        content: (done && done.reply) || last.content,
        suggestions: done && Array.isArray(done.suggestions) ? done.suggestions : [],
        widgets: done && Array.isArray(done.widgets) ? done.widgets : [],
        streaming: false
      }));
      const newId = done && done.conversationId;
      if (newId && newId !== conversationId) setConversationId(newId);
    } catch (err) {
      if (err && (err.name === 'AbortError' || err.code === 'STREAM_ABORTED')) {
        stopped = true;
        patchLastAssistant((last) => ({ ...last, streaming: false, content: last.content || '(interrotto)' }));
      } else if (err instanceof ApiError && (err.statusCode === 451 || err.code === 'FORBIDDEN')) {
        toast.error('Consenso scaduto. Per favore riavvia la conversazione.');
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        onSessionEnded();
        return;
      } else {
        toast.error(err instanceof ApiError ? err.message : (err.message || t('common.error')));
        setMessages((prev) => {
          const next = [...prev];
          if (next.length && next[next.length - 1].role === 'assistant' && next[next.length - 1].streaming) next.pop();
          if (next.length && next[next.length - 1].role === 'user') next.pop();
          return next;
        });
        setDraft(text);
      }
    } finally {
      abortRef.current = null;
      setSending(false);
      if (stopped) { /* keep partial answer */ }
    }
  };

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    sendText(text);
  };

  const stopGeneration = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const endSession = useMutation({
    mutationFn: () => kioskApi.revoke(session.customerSessionId, 'customer_ended'),
    onSettled: () => {
      if (voiceLive) voice.stop();
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      onSessionEnded();
    }
  });

  const onModeChange = (next) => {
    if (next !== 'voice' && voiceLive) voice.stop();
    setMode(next);
  };
  const startVoice = () => voice.start({ cs: session.customerSessionId });

  // Fall back to text if voice becomes unavailable while selected.
  useEffect(() => {
    if (!voiceAllowed && mode === 'voice') {
      if (voiceLive) voice.stop();
      setMode('text');
    }
  }, [voiceAllowed, mode, voiceLive, voice]);

  const modeItems = [{ value: 'text', label: 'Testo' }];
  if (voiceAllowed) modeItems.push({ value: 'voice', label: 'Voce' });

  return (
    <div className="mx-auto flex h-full min-h-0 max-w-3xl flex-col px-6 py-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success/60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
            </span>
            Sessione privata
          </span>
          {purposes.map((p) => {
            const info = PURPOSE_LABEL[p];
            if (!info) return null;
            const { label, Icon } = info;
            return (
              <span
                key={p}
                className="inline-flex items-center gap-1.5 rounded-full border border-success/20 bg-success/5 px-2.5 py-1 text-xs font-medium text-success"
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {label}
              </span>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          {voiceAllowed && (
            <Tabs value={mode} onChange={onModeChange} items={modeItems} />
          )}
          <Button variant="ghost" size="sm" onClick={() => setConfirmEnd(true)}>Termina</Button>
        </div>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col p-0">
        {mode === 'text' ? (
          <>
            <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-5">
              {messages.length === 0 ? (
                <div className="flex h-full items-center justify-center">
                  <div className="max-w-md text-center">
                    <p className="text-base text-ink">Ciao 👋</p>
                    <p className="mt-2 text-sm text-ink-muted">
                      Sono l'assistente del negozio. Dimmi che tipo di animale hai (specie, razza, età, peso, eventuali esigenze)
                      e ti consiglierò i prodotti più adatti.
                    </p>
                  </div>
                </div>
              ) : (
                <ul className="flex flex-col gap-4">
                  {messages.map((m, idx) => (
                    <li key={idx}>
                      <MessageBubble
                        role={m.role}
                        content={m.content}
                        streaming={m.streaming}
                        suggestions={idx === messages.length - 1 ? m.suggestions : undefined}
                        widgets={m.widgets}
                        customerSessionId={session.customerSessionId}
                        onSuggestion={(s) => sendText(s)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border-t border-border p-3">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Composer value={draft} onChange={setDraft} onSubmit={submit} sending={sending} />
                </div>
                {sending && (
                  <Button variant="secondary" size="sm" onClick={stopGeneration} className="mb-1">
                    <Square className="h-3.5 w-3.5 fill-current" /> Ferma
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center justify-center gap-4 border-b border-border py-8">
              <VoiceOrb status={voice.status} speaking={voice.speaking} userSpeaking={voice.userSpeaking} />
              <div className="flex items-center gap-2">
                {voiceLive ? (
                  <>
                    <Button variant="secondary" size="sm" onClick={voice.toggleMute}>
                      {voice.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                      {voice.muted ? 'Microfono off' : 'Microfono on'}
                    </Button>
                    <Button variant="danger" size="sm" onClick={voice.stop}><PhoneOff className="h-4 w-4" /> Termina</Button>
                  </>
                ) : (
                  <Button size="sm" onClick={startVoice}><Phone className="h-4 w-4" /> Avvia conversazione</Button>
                )}
              </div>
              {voice.error && <p className="text-xs text-danger">{voice.error}</p>}
            </div>
            <TranscriptPanel transcript={voice.transcript} />
          </>
        )}
      </Card>

      <ConfirmDialog
        open={confirmEnd}
        title="Terminare la conversazione?"
        description="Tutti i dati di questa sessione anonima saranno cancellati."
        destructive
        confirmLabel="Termina"
        loading={endSession.isPending}
        onCancel={() => setConfirmEnd(false)}
        onConfirm={() => endSession.mutate()}
      />
    </div>
  );
}
